import { Collapse, Grid, IconButton } from '@material-ui/core';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import copy from 'copy-to-clipboard';
import { isEmpty } from 'lodash-es';
import MaterialTable, { Column } from 'material-table';
import moment from 'moment';
import React from 'react';
import ReactJson, { CollapsedFieldProps, OnCopyProps } from 'react-json-view';
// @ts-ignore
import XMLViewer from 'react-xml-viewer';

import { IDemandSourceNameMap } from '~/src/dash/api/console-api';
import { IEndpointResponseUIData } from '~/src/dash/api/live_logs/models';
import { CopyButton } from '~/src/dash/components/button';
import { IStore } from '~/src/dash/reducers/store';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { utils } from '~/src/dash/utils/json';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';

interface IRow {
  timestamp: string;
  deviceID: string;
  response: string;
  EndpointResponseAds: string;
  endpointURI: string;
  endpointUUID: string;
  vmapUUID: string;
  response_type: string;
  // auctionRequester: string;
  endpointBody: string;
  idx: number;
}

export interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  data: IEndpointResponseUIData[];
}

interface IState extends IBaseState {
  tableColumns: Array<Column<IRow>>;
  tableData: IRow[];
  activeIndex: number;
}

type Globals = '-moz-initial' | 'inherit' | 'initial' | 'revert' | 'unset';
type WhiteSpaceProperty =
  | Globals
  | '-moz-pre-wrap'
  | 'break-spaces'
  | 'normal'
  | 'nowrap'
  | 'pre'
  | 'pre-line'
  | 'pre-wrap';

function dataToRow(
  data: IEndpointResponseUIData[],
  publisherTimezone: string,
  isSuperUser: boolean,
  demandSourceNameMap: IDemandSourceNameMap,
): IRow[] {
  if (!Array.isArray(data) || data === undefined) {
    return [];
  }

  let i = -10;
  const mappedData = (data || [])
    .map((d: IEndpointResponseUIData): IRow => {
      i = i + 10;

      // get response
      let response = d.response;
      const responseParsed = utils.json.SafeParse(response);
      if (responseParsed !== undefined && responseParsed.response !== undefined) {
        response = responseParsed.response;
      }

      // Compute type of response
      let response_type = 'xml';
      if (response !== undefined && response !== '' && response[0] !== '<') {
        response_type = 'json';
      }

      const formattedEndpointResponseAds = [];
      if (d.EndpointResponseAds) {
        const endpointResponseAds = JSON.parse(JSON.stringify(d.EndpointResponseAds));
        for (const endpointResponseAd of endpointResponseAds) {
          if (demandSourceNameMap[endpointResponseAd.DemandSource]) {
            endpointResponseAd.DemandSource = demandSourceNameMap[endpointResponseAd.DemandSource];
          }
          if (!isSuperUser) {
            delete endpointResponseAd.RecognitionResult;
          }
          formattedEndpointResponseAds.push(endpointResponseAd);
        }
      }

      return {
        timestamp:
          moment(d.timestamp).tz(publisherTimezone).format('DD-MM-YYYY[\n]HH:mm:ss') +
          '\n' +
          publisherTimezone,
        deviceID: d.deviceID,
        endpointURI: d.endpointURI,
        response,
        response_type,
        // auctionRequester: d.auctionRequester === '' ? 'Standard' : d.auctionRequester,
        endpointBody: d.endpointBody,
        idx: i,
        EndpointResponseAds: JSON.stringify(formattedEndpointResponseAds),
        endpointUUID: d.endpointUUID,
        vmapUUID: d.vmapUUID,
      } as IRow;
    })
    .filter((row: IRow) => row.response !== '{}' && row.response !== `<VAST version="3.0"></VAST>`)
    .sort((a: IRow, b: IRow): number => {
      // Sort data by timestamp desc
      return (
        moment(b.timestamp, 'DD-MM-YYYY HH:mm:ss').unix() -
        moment(a.timestamp, 'DD-MM-YYYY HH:mm:ss').unix()
      );
    });

  return mappedData;
}

export const LogEndpointResponseTable = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      button: {
        margin: theme.spacing.unit,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      tableColumns: [
        {
          title: 'Timestamp',
          field: 'timestamp',
          searchable: false,
          width: '40px',
          cellStyle: { whiteSpace: 'pre-wrap' as WhiteSpaceProperty },
        },
        { title: 'Endpoint URI', field: 'endpointURI', searchable: false },
        // { title: 'Request Format', field: 'auctionRequester', searchable: false },
      ],
      tableData: dataToRow(
        this.props.data,
        this.props.session.publisher.Timezone,
        hasRoleSuperUser(this.props.user.user),
        this.props.session.demandSourceNameMap,
      ),
      activeIndex: -1,
    };

    handleCopy = (copyObj: OnCopyProps) => {
      copy(JSON.stringify(copyObj.src, null, '\t'));
    };

    handleClick = (index: number) => {
      const { activeIndex } = this.state;
      const newIndex = activeIndex === index ? -1 : index;

      this.setState({ activeIndex: newIndex });
    };

    componentDidUpdate(prevProps: Readonly<IProps>): void {
      if (prevProps.data !== this.props.data) {
        this.setState({
          tableData: dataToRow(
            this.props.data,
            this.props.session.publisher.Timezone,
            hasRoleSuperUser(this.props.user.user),
            this.props.session.demandSourceNameMap,
          ),
        });
      }
    }

    render() {
      const displayDetailPanel =
        this.state.tableData.find((row: IRow) => row.endpointBody !== '') !== undefined;

      return (
        <div>
          <MaterialTable
            detailPanel={(rowData: IRow) => {
              return (
                <Grid container direction='column'>
                  {/* Device ID */}
                  <Grid item>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item style={{ marginLeft: '48px' }}>
                        <p>Device ID</p>
                      </Grid>
                      <Grid item>
                        <CopyButton copy={rowData.deviceID} text='Copy' />
                      </Grid>
                      <Grid item>{rowData.deviceID}</Grid>
                    </Grid>
                  </Grid>
                  {/* Endpoint UUID */}
                  <Grid item>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item style={{ marginLeft: '48px' }}>
                        <p>Endpoint UUID</p>
                      </Grid>
                      <Grid item>
                        <CopyButton copy={rowData.endpointUUID} text='Copy' />
                      </Grid>
                      <Grid item>{rowData.endpointUUID}</Grid>
                    </Grid>
                  </Grid>
                  {/* vmap UUID */}
                  {!isEmpty(rowData.vmapUUID) && (
                    <Grid item>
                      <Grid container direction='row' justify='flex-start' alignItems='center'>
                        <Grid item style={{ marginLeft: '48px' }}>
                          <p>VMAP UUID</p>
                        </Grid>
                        <Grid item>
                          <CopyButton copy={rowData.vmapUUID} text='Copy' />
                        </Grid>
                        <Grid item>{rowData.vmapUUID}</Grid>
                      </Grid>
                    </Grid>
                  )}
                  {/* Query Body*/}
                  {displayDetailPanel && (
                    <Grid item>
                      <Grid container direction='row' justify='flex-start' alignItems='center'>
                        <Grid item>
                          <IconButton onClick={() => this.handleClick(rowData.idx + 2)}>
                            {this.state.activeIndex === rowData.idx + 2 ? (
                              <ExpandLess />
                            ) : (
                              <ExpandMore />
                            )}
                          </IconButton>
                        </Grid>
                        <Grid item>
                          <p>Request Body</p>
                        </Grid>
                        <Grid item>
                          <CopyButton copy={rowData.endpointBody} text='Copy' />
                        </Grid>
                      </Grid>
                      <Collapse in={this.state.activeIndex === rowData.idx + 2}>
                        <ReactJson
                          displayDataTypes={false}
                          src={utils.json.SafeParse(rowData.endpointBody)}
                          enableClipboard={this.handleCopy}
                        />
                      </Collapse>
                    </Grid>
                  )}
                  {/* Response*/}
                  <Grid item>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item>
                        <IconButton onClick={() => this.handleClick(rowData.idx + 1)}>
                          {this.state.activeIndex === rowData.idx + 1 ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <p>Response</p>
                      </Grid>
                      <Grid item>
                        <CopyButton copy={rowData.response} text='Copy' />
                      </Grid>
                    </Grid>
                    <Collapse in={this.state.activeIndex === rowData.idx + 1}>
                      {rowData.response_type === 'json' ? (
                        <ReactJson
                          displayDataTypes={false}
                          src={utils.json.SafeParse(rowData.response)}
                          enableClipboard={this.handleCopy}
                        />
                      ) : (
                        <XMLViewer xml={rowData.response} invalidXml={<p>{rowData.response}</p>} />
                      )}
                    </Collapse>
                  </Grid>
                  {/* Winners Json*/}
                  <Grid item>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item>
                        <IconButton onClick={() => this.handleClick(rowData.idx + 3)}>
                          {this.state.activeIndex === rowData.idx + 3 ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <p>Adbreak</p>
                      </Grid>
                      <Grid item>
                        <CopyButton copy={rowData.EndpointResponseAds} text='Copy' />
                      </Grid>
                    </Grid>
                    <Collapse in={this.state.activeIndex === rowData.idx + 3}>
                      <ReactJson
                        displayDataTypes={false}
                        src={utils.json.SafeParse(rowData.EndpointResponseAds)}
                        enableClipboard={this.handleCopy}
                        shouldCollapse={(field: CollapsedFieldProps): boolean =>
                          field.type === 'array' && field.name === 'MediaFileURLs'
                        }
                      />
                    </Collapse>
                  </Grid>
                </Grid>
              );
            }}
            style={{
              paddingLeft: '20px',
              paddingRight: '20px',
            }}
            title=''
            options={{
              search: false,
              pageSize: 15,
              pageSizeOptions: [5, 15, 30],
              toolbar: false,
            }}
            columns={this.state.tableColumns}
            data={this.state.tableData}
          />
        </div>
      );
    }
  },
);
